import { useEffect, useRef } from 'react'
import {
  COVERAGE_LABEL,
  type Benefit,
  type Person,
  type Policy,
} from '@asvise/household-engine/vocabulary'

/**
 * One policy in full, over the screen that lists them.
 *
 * The list says a product, an insurer, a number and how many benefits. What a
 * household actually wants when it clicks one is what the document said: who is
 * covered for what, for how much, from when to when, what it costs, what it
 * leaves them to pay, and the line and page each figure was read from. All of
 * it was already on the record and none of it was visible anywhere, since the
 * record's group of policies went.
 *
 * A sheet rather than a screen, for the reason the finding sheet is one: the
 * list stays where it was, dismissing returns to the row, and Esc, the backdrop
 * and focus trapping come with the native dialog.
 *
 * NOTHING IS SUMMED. Benefits that draw on one pool are shown as claims against
 * it, with the pool's own note, because a schedule that prints S$100,000,
 * S$100,000 and S$50,000 does not describe S$250,000 of protection and a card
 * that listed three amounts in a column would invite exactly that reading.
 *
 * UNKNOWN IS SAID. The engine keeps "nobody has established this" apart from
 * "none apply", and so does this: an absent term reads "Not stated", never a
 * blank and never a zero.
 */

const money = (n: number) => `S$${Math.round(n).toLocaleString('en-SG')}`

const UNKNOWN = 'UNKNOWN'

/** A value as a person would say it, with the unknown and the absent both said. */
function said(value: unknown, format: (v: never) => string = String): string {
  if (value === null || value === undefined || value === UNKNOWN) return 'Not stated'
  return format(value as never)
}

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' })

const SOURCE_LABEL: Record<Policy['source'], string> = {
  PERSONAL: 'A policy you hold yourself',
  EMPLOYER: 'Cover through an employer',
  PUBLIC_SCHEME: 'A public scheme',
}

const FREQUENCY_LABEL: Record<Policy['premium']['frequency'], string> = {
  MONTHLY: 'a month',
  QUARTERLY: 'a quarter',
  HALF_YEARLY: 'every six months',
  YEARLY: 'a year',
  SINGLE: 'once',
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="policy-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  )
}

/** Who a benefit insures, with the printed name beside it where they differ. */
function insured(benefit: Benefit, people: Person[]): string {
  const matched = people.find((p) => p.id === benefit.insured_person_id)
  if (matched) {
    return benefit.insured_as_printed && benefit.insured_as_printed !== matched.label
      ? `${matched.label} (printed as ${benefit.insured_as_printed})`
      : matched.label
  }
  // Said, because cover nobody has matched counts towards nothing and a row
  // that read as covered would be the screen contradicting the engine.
  return benefit.insured_as_printed
    ? `${benefit.insured_as_printed}, not matched to anybody in your family`
    : 'Not matched to anybody in your family'
}

/** When a benefit stops, where the schedule says. Null otherwise. */
function stoppedOn(timing: Benefit['timing']): string | null {
  if (typeof timing.expires_on === 'string' && timing.expires_on !== UNKNOWN) {
    return day(timing.expires_on)
  }
  if (typeof timing.expires_at_age === 'number') return `At age ${timing.expires_at_age}`
  return null
}

function BenefitCard({
  benefit,
  policy,
  people,
}: {
  benefit: Benefit
  policy: Policy
  people: Person[]
}) {
  const pool = benefit.pool_id ? policy.pools.find((p) => p.id === benefit.pool_id) : undefined
  const shares = benefit.cost_sharing
  const timing = benefit.timing

  return (
    <li className="policy-benefit">
      <h4>{COVERAGE_LABEL[benefit.code]}</h4>
      <dl className="policy-facts">
        <Fact label="Insures">{insured(benefit, people)}</Fact>

        {benefit.amount !== null && (
          <Fact label="Sum">
            {typeof benefit.amount === 'number' ? money(benefit.amount) : 'No amount established'}
            {/*
              Only where the benefit is not in a pool. The engine marks every
              member of a pool ACCELERATED whatever it said about itself,
              because the pool is the stronger evidence, so on a policy where
              death, disability and critical illness share one sum this line
              told a household its death benefit is "paid early out of another
              benefit". It is the base sum. The pool line below says the true
              thing for a pooled benefit, and this is for the case where a
              schedule says a benefit is accelerated and names no pool.
            */}
            {benefit.basis === 'ACCELERATED' && !pool && (
              <span className="policy-note">
                Paid early out of another benefit, so it reduces what is left rather than adding
                to it.
              </span>
            )}
          </Fact>
        )}

        {benefit.annual_limit !== undefined && (
          <Fact label="Yearly limit">{said(benefit.annual_limit, money)}</Fact>
        )}

        {pool && (
          <Fact label="Shared with">
            <span className="policy-note-first">{pool.note}</span>
          </Fact>
        )}

        {shares && (
          <>
            <Fact label="You pay first">{said(shares.deductible, money)} each policy year</Fact>
            <Fact label="Then you share">
              {said(shares.co_insurance, (v: number) => `${Math.round(v * 100)}% of the rest`)}
            </Fact>
            <Fact label="A rider buys this down">
              {said(shares.rider_reduces_cost_sharing, (v: boolean) => (v ? 'Yes' : 'No'))}
            </Fact>
            {shares.out_of_pocket_cap !== undefined && (
              <Fact label="Most you pay in a year">{said(shares.out_of_pocket_cap, money)}</Fact>
            )}
          </>
        )}

        <Fact label="Waiting period">
          {said(timing.waiting_period_days, (v: number) => `${v} days before it applies`)}
        </Fact>
        <Fact label="Survival period">
          {said(timing.survival_period_days, (v: number) => `${v} days after you qualify`)}
        </Fact>
        {/* Only when the schedule states one. "Not stated" on a whole life
            death benefit reads as a gap in the policy rather than as the absence
            of an expiry the policy was never going to have. */}
        {stoppedOn(timing) !== null && <Fact label="Stops">{stoppedOn(timing)}</Fact>}
      </dl>

      {/*
        The line and the page, which is the thing that makes this a reading
        rather than an assertion. A figure either carries the words it came from
        or says it does not, and a household can tell which.
      */}
      <p className="quote">
        <span className="where">
          {benefit.citation?.document ?? policy.product_name}
          {benefit.citation?.page != null && `, page ${benefit.citation.page}`}
        </span>
        {benefit.citation?.verbatim ?? 'No quoted line was recorded for this.'}
      </p>
    </li>
  )
}

export function PolicySheet({
  policy,
  people,
  busy,
  onOwner,
  onClose,
}: {
  policy: Policy
  people: Person[]
  /** A save is in flight, so the control waits rather than racing it. */
  busy: boolean
  /** Match the contract to a member, or to nobody. */
  onOwner: (owner: string | null) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    ref.current?.showModal()
  }, [])

  const review = policy.review_required ?? []

  return (
    <dialog
      ref={ref}
      className="sheet finding-sheet"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) ref.current?.close()
      }}
    >
      <div className="sheet-body">
        <header className="answering-head">
          <span className="answering-where">
            {[policy.insurer, policy.policy_number].filter(Boolean).join(' · ') || 'Policy'}
          </span>
          <button
            type="button"
            className="sheet-close"
            onClick={() => ref.current?.close()}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <h2 className="policy-title">{policy.product_name || 'A policy'}</h2>

        {/*
          Said first, above everything else, where something about the document
          means its figures are not being counted. Burying "counted towards no
          shortfall" under the terms would be the screen reporting figures the
          engine has quietly set aside.
        */}
        {review.length > 0 && (
          <p className="policy-review">
            Needs a look: {review.join(' ')} Amounts from this document are reported and counted
            towards no shortfall.
          </p>
        )}

        <dl className="policy-facts">
          <Fact label="Kind of cover">{SOURCE_LABEL[policy.source]}</Fact>
          {/* A choice, and not a statement. The review step asks who owns the
              contract and defaults to nobody, and this is the only way back to
              it once the policy is on the record. The printed name stays beside
              it, as the benefits keep theirs, so the match can be checked
              against what the document says.

              Without the "not matched" warning the benefits carry: cover
              nobody has matched counts towards nothing, which is why that is
              said there, and who owns the contract changes no figure. */}
          <Fact label="Held by">
            <select
              className="policy-owner"
              aria-label="Who holds this policy"
              disabled={busy}
              value={
                people.some((p) => p.id === policy.policyholder_person_id)
                  ? (policy.policyholder_person_id ?? '')
                  : ''
              }
              onChange={(e) => onOwner(e.target.value === '' ? null : e.target.value)}
            >
              <option value="">Not matched to anybody in your family</option>
              {people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.label}
                </option>
              ))}
            </select>
            {policy.policyholder_as_printed && (
              <span className="policy-note">
                The document names {policy.policyholder_as_printed}. This records who holds the
                contract and changes no figure.
              </span>
            )}
          </Fact>
          <Fact label="Death benefit goes to">{said(policy.beneficiary_as_printed)}</Fact>
          <Fact label="Starts">{said(policy.start_date, day)}</Fact>
          {/* Null is a whole life policy and UNKNOWN is a document that did not
              say, which are different things and read differently. */}
          <Fact label="Cover ends">
            {policy.end_date === null ? 'No end date: it runs for life' : said(policy.end_date, day)}
          </Fact>
          <Fact label="Premiums stop">{said(policy.premium_term_end, day)}</Fact>
          <Fact label="Premium">
            {typeof policy.premium.amount === 'number'
              ? `${money(policy.premium.amount)} ${FREQUENCY_LABEL[policy.premium.frequency]}`
              : 'Not stated'}
            <span className="policy-note">
              {policy.premium.guaranteed === true
                ? 'The rate is guaranteed.'
                : policy.premium.guaranteed === false
                  ? 'The rate is not guaranteed and can change.'
                  : 'Whether the rate is guaranteed is not stated.'}
            </span>
          </Fact>
          {policy.source === 'EMPLOYER' && (
            <Fact label="Continues if you leave the job">
              {said(policy.portable, (v: boolean) => (v ? 'Yes, the documents say so' : 'No'))}
            </Fact>
          )}
        </dl>

        <h3 className="policy-section">What it pays</h3>
        {policy.benefits.length === 0 ? (
          <p className="note">No benefit was read from this document.</p>
        ) : (
          <ul className="policy-benefits">
            {policy.benefits.map((benefit, i) => (
              <BenefitCard key={i} benefit={benefit} policy={policy} people={people} />
            ))}
          </ul>
        )}

        {policy.exclusions.length > 0 && (
          <>
            <h3 className="policy-section">What it does not cover</h3>
            {policy.exclusions.map((exclusion, i) => (
              <p key={i} className="quote">
                <span className="where">
                  {exclusion.citation?.page != null
                    ? `Page ${exclusion.citation.page}`
                    : 'The document'}
                </span>
                {exclusion.verbatim}
              </p>
            ))}
          </>
        )}

        {policy.extraction_warnings.length > 0 && (
          <>
            <h3 className="policy-section">What the reader could not settle</h3>
            <ul className="uncertain">
              {policy.extraction_warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </>
        )}

        {/*
          How it got here, which was the one thing the record's group of
          policies said that nothing else did. Read from a document and accepted
          by a person line by line, or typed by hand, with when it was confirmed.
        */}
        <p className="policy-origin">
          {policy.read_by_version
            ? 'Read from a document you uploaded, and accepted by you line by line.'
            : 'Entered by hand.'}{' '}
          {policy.confirmed_at ? `Confirmed ${day(policy.confirmed_at)}.` : 'Not yet confirmed.'}
        </p>
      </div>
    </dialog>
  )
}
